/* eslint-disable react/forbid-prop-types */
import React, { useCallback, useEffect, useMemo } from 'react'
import { Controller, useFormContext } from 'react-hook-form'

import { FormHelperText } from '@mui/material'
import { InputLabel, MenuItem, Select as MuiSelect } from '@material-ui/core'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import selectStyles from './styles'
import generalInputStyles from '../styles'
import { menuProps } from '_/views/step-notification-message-setup/utils'
import HelpInfoButton from '_/components/button/help-info-button'

/**
 * Renders a Select component with a label, options, and validation rules.
 * @param {Object} props - The props object.
 * @param {Object} props.menuProps - The props for the menu.
 * @param {Array} props.options - The array of options to be displayed in the select.
 * @param {string} props.label - The label for the select.
 * @param {string} props.name - The name of the select. (Hook-Form related)
 * @param {boolean} [props.required=false] - Whether the select is required or not.
 * @param {Function} props.getValue - The function to get the value of an option. (MenuItem related)
 * @param {Function} [props.getLabel=()=>{}] - The function to get the label of an option. (MenuItem related)
 * @param {Function} props.getKey - The function to get the key of an option. (MenuItem related. Each option must have a unique key)
 * @param {string} [props.defaultValue=''] - The default value of the select.
 * @param {boolean} [props.disabled=false] - Whether the select is disabled or not.
 * @param {Object} [props.style={}] - The style object for the select container.
 * @param {string} [props.containerClasses=null] - The classes for the select container.
 * @param {string} [props.labelClasses=null] - The classes for the select label.
 * @param {React.ReactNode} [props.labelAddon=null] - Optional content rendered next to the label.
 * @param {string} [props.selectClasses=null] - The classes for the Mui select component.
 * @param {Object} [props.helpInfoButton=null] - The HelpInfoButton configuration object with title, content, optional style, and optional className.
 * @returns {React.ReactElement} - The Select component.
 */
function Select({
  options,
  label,
  name,
  required = false,
  getValue,
  getLabel = () => {},
  getKey,
  defaultValue = '',
  disabled = false,
  style = {},
  containerClasses,
  customErrorMessage = '',
  labelClasses,
  labelAddon = null,
  selectClasses,
  emptyValue = '',
  helpInfoButton = null,
  ...rest
}) {
  const generalStyles = generalInputStyles()
  const localStyles = selectStyles()
  const { control, errors, setValue } = useFormContext()
  const { onChange: externalOnChange, ...muiSelectProps } = rest

  useEffect(() => {
    setValue(name, defaultValue)
  }, [defaultValue, name, setValue])

  const generateOptionKey = useCallback(
    item => {
      return getKey ? getKey(item) : crypto.randomUUID()
    },
    [getKey]
  )

  const errorMessage = useMemo(
    () => errors?.[`${name}`]?.message || customErrorMessage,
    [errors?.[name], customErrorMessage]
  )

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '80px',
        justifyContent: 'center',
        ...style,
      }}
      className={classNames(localStyles.select, containerClasses ?? null)}
    >
      <InputLabel
        variant="standard"
        id={`${name}-label`}
        error={!!errorMessage}
        disabled={disabled}
        style={{ color: errorMessage ? '#C90000' : null }}
        className={classNames(localStyles.selectHeightCompensation, labelClasses ?? null)}
        shrink
      >
        {label}
        {labelAddon}
        {required && <span className={generalStyles.required}>*</span>}
        {helpInfoButton && <HelpInfoButton {...helpInfoButton} />}
      </InputLabel>
      <Controller
        control={control}
        name={name}
        defaultValue={defaultValue ?? ''}
        rules={{ required: { value: required, message: 'Esse campo é obrigatório' } }}
        render={({ onChange, value, name: fieldName }) => (
          <MuiSelect
            MenuProps={menuProps}
            displayEmpty
            labelId={`${name}-label`}
            required={required}
            disabled={disabled}
            error={!!errors[name]}
            className={selectClasses ?? null}
            value={value ?? ''}
            onChange={event => {
              externalOnChange?.(event)
              onChange(event.target.value)
            }}
            inputProps={{
              'data-field-path': fieldName,
            }}
            {...muiSelectProps}
          >
            {!!emptyValue.length && (
              <MenuItem key="empty" value="" style={{ color: '#ccc' }} selected>
                {emptyValue}
              </MenuItem>
            )}
            {options?.length ? (
              options.map(item => (
                <MenuItem key={generateOptionKey(item)} value={getValue(item)}>
                  {getLabel(item) || getValue(item)}
                </MenuItem>
              ))
            ) : (
              <MenuItem value="" disabled>
                Nenhum item encontrado
              </MenuItem>
            )}
          </MuiSelect>
        )}
      />
      <FormHelperText className={localStyles.errorMessage} error={!!errorMessage}>
        {errorMessage}
      </FormHelperText>
    </div>
  )
}

Select.propTypes = {
  menuProps: PropTypes.object.isRequired,
  options: PropTypes.array.isRequired,
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  required: PropTypes.bool,
  getValue: PropTypes.func.isRequired,
  getLabel: PropTypes.func,
  getKey: PropTypes.func.isRequired,
  defaultValue: PropTypes.string,
  disabled: PropTypes.bool,
  style: PropTypes.object,
  containerClasses: PropTypes.string,
  labelClasses: PropTypes.string,
  labelAddon: PropTypes.node,
  selectClasses: PropTypes.string,
  helpInfoButton: PropTypes.shape({
    title: PropTypes.string.isRequired,
    content: PropTypes.string.isRequired,
    style: PropTypes.object,
    className: PropTypes.string,
  }),
}

Select.defaultProps = {
  required: false,
  defaultValue: '',
  disabled: false,
  style: {},
  containerClasses: null,
  labelClasses: null,
  labelAddon: null,
  selectClasses: null,
  getLabel: () => {},
  helpInfoButton: null,
}

export default Select
